import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useBillingMaintenance } from '../hooks/useAppConfig';
import { TIERS, TIER_ORDER } from '../config/pricing';
import {
  Package,
  TrendingUp,
  Receipt,
  Truck,
  BarChart3,
  Check,
  ArrowRight,
  Zap,
  Menu,
  X,
  Mail,
  AlertCircle,
  Twitter,
  ChevronDown,
  Sparkles,
  ShieldCheck,
  Clock,
  DollarSign,
  FileSpreadsheet,
  Smartphone,
} from 'lucide-react';

// Discord brand SVG — used in the "More ways to work" section. Lucide
// has MessageSquare but the real Discord mark is more recognizable.
const DiscordMark = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

/* ============================================================
 * Marketing page — adopts the app's design tokens
 *
 * Replaces the previous hardcoded #0a1628 / #1e3a5f navy palette
 * with the same CSS variables the rest of the app uses
 * (var(--color-card), rgb(var(--accent-primary)), etc.) — so the
 * marketing site picks up the user's theme + accent and reads as
 * the same product.
 *
 * Sections (top → bottom):
 *   1. Nav            — sticky, glassy
 *   2. Hero           — copy on the left, live animated dashboard on the right
 *   3. Social proof   — single bar with built-by-resellers framing
 *   4. Value props    — three pillars (replaces 7-card bento)
 *   5. Walkthrough    — tabbed screenshots (replaces 3 stacked previews)
 *   6. Pricing        — 2 tiers (Free + Plus), accent-aware highlight on Plus
 *   7. FAQ            — accordion answering common objections
 *   8. Final CTA      — full-bleed accent gradient
 *   9. Footer
 * ============================================================ */

// CSS keyframes — limited set. Most motion happens via framer-motion now.
const animationStyles = `
@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 8px 32px rgb(var(--accent-primary) / 0.30); }
  50%      { box-shadow: 0 12px 48px rgb(var(--accent-primary) / 0.50); }
}
@keyframes bounceArrow {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(4px); }
}
@keyframes drawLine {
  to { stroke-dashoffset: 0; }
}
`;

/* ============================================================
 * HeroLiveDashboard — the centerpiece of the hero
 *
 * Mini Nexus dashboard card with motion that proves the product
 * is real: three KPIs count up from 0 to target on mount, a small
 * sparkline draws itself in left-to-right, and a "recent activity"
 * row cycles every ~3 seconds. Pauses animations when the widget
 * scrolls out of view so it isn't a perpetual frame-rate cost.
 * ============================================================ */
/* ============================================================
 * Billing-maintenance helpers used by the pricing section
 *
 * Two tiny components so the pricing JSX stays readable: an inline
 * banner above the cards explaining the state, and a CTA wrapper that
 * swaps the per-tier button into a disabled "Under maintenance" state.
 *
 * Both read the same realtime flag from app_config (via
 * useBillingMaintenance). When admin flips the toggle in
 * AdminSettingsPage → App Settings, every connected client updates
 * instantly without a page reload.
 * ============================================================ */
function BillingMaintenanceInlineBanner() {
  const { enabled, message } = useBillingMaintenance();
  if (!enabled) return null;
  return (
    <div
      className="max-w-[700px] mx-auto mb-8 rounded-md border p-3 flex items-center gap-2 text-xs sm:text-sm"
      style={{
        backgroundColor: 'color-mix(in srgb, #f59e0b 10%, var(--color-card))',
        borderColor: 'color-mix(in srgb, #f59e0b 35%, var(--color-border))',
        color: 'var(--color-text)',
      }}
    >
      <span
        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: '#f59e0b' }}
      />
      <span className="font-medium" style={{ color: '#f59e0b' }}>Heads up:</span>
      <span className="flex-1">{message}</span>
    </div>
  );
}

function PricingCardCta({ tier, isHL, onSelect }) {
  const { enabled } = useBillingMaintenance();
  if (enabled) {
    return (
      <button
        disabled
        className="w-full py-2 rounded-md font-medium text-sm mb-4 cursor-not-allowed opacity-60"
        style={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'var(--color-border)',
          color: 'var(--color-text-muted)',
          backgroundColor: 'var(--color-bg)',
        }}
        title="Billing changes are temporarily under maintenance."
      >
        Under maintenance
      </button>
    );
  }
  return (
    <button
      onClick={onSelect}
      className="w-full py-2 rounded-md font-medium text-sm transition-colors mb-4"
      style={
        isHL
          ? { backgroundColor: 'rgb(var(--accent-primary))', color: '#fff' }
          : { borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--color-border)', color: 'var(--color-text)', backgroundColor: 'transparent' }
      }
    >
      {tier.cta}
    </button>
  );
}

function HeroLiveDashboard() {
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { once: false, margin: '-100px' });

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-md border shadow-2xl overflow-hidden"
      style={{
        background: `linear-gradient(180deg, var(--color-card) 0%, color-mix(in srgb, rgb(var(--accent-primary)) 4%, var(--color-card)) 100%)`,
        borderColor: 'var(--color-border)',
      }}
    >
      {/* Faux browser chrome — keeps the dashboard reading as "in app" */}
      <div
        className="px-3 py-2 border-b flex items-center gap-2"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'color-mix(in srgb, var(--color-card) 80%, transparent)' }}
      >
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
        </div>
        <div
          className="ml-2 flex-1 text-[10px] font-mono px-2 py-0.5 rounded"
          style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text-muted)' }}
        >
          nexusmanager.app/dashboard
        </div>
        <div className="text-[10px] font-medium flex items-center gap-1" style={{ color: 'rgb(var(--accent-primary))' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Live
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* KPI row — three tiles with count-up animation */}
        <div className="grid grid-cols-3 gap-2.5">
          <KpiTile label="Revenue (mo)" prefix="$" target={12483} delay={0.15} inView={inView} />
          <KpiTile label="Items in stock" target={342} delay={0.3} inView={inView} />
          <KpiTile label="Net profit" prefix="$" target={4127} delay={0.45} inView={inView} accent />
        </div>

        {/* Sparkline — draws in left-to-right */}
        <MiniChart inView={inView} />

        {/* Recent activity — cycles through entries every ~3s */}
        <RecentActivity inView={inView} />
      </div>
    </div>
  );
}

function KpiTile({ label, prefix = '', target, delay = 0, inView, accent = false }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    // Eased count-up: more frames early, fewer late so big numbers feel snappy.
    let frame = 0;
    const totalFrames = 50;
    const startDelay = setTimeout(() => {
      const interval = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setValue(Math.round(target * eased));
        if (frame >= totalFrames) clearInterval(interval);
      }, 16);
      return () => clearInterval(interval);
    }, delay * 1000);
    return () => clearTimeout(startDelay);
  }, [inView, target, delay]);

  const formatted = value.toLocaleString('en-US');

  return (
    <div
      className="rounded-md border p-2.5"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-card) 70%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </div>
      <div
        className="text-lg sm:text-xl font-semibold tabular-nums"
        style={{ color: accent ? 'rgb(var(--accent-primary))' : 'var(--color-text)' }}
      >
        {prefix}{formatted}
      </div>
    </div>
  );
}

function MiniChart({ inView }) {
  // Sparkline data — 12 points loosely trending up. Hardcoded so the
  // chart is reproducible; the "draw in" is the animation, not the data.
  const points = [25, 28, 22, 35, 40, 38, 52, 48, 60, 65, 72, 80];
  const max = Math.max(...points);
  const width = 280;
  const height = 60;
  const stepX = width / (points.length - 1);

  // Build the polyline path
  const path = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${height - (v / max) * (height - 8) - 4}`)
    .join(' ');

  // Build a closed area path for the fill (down to bottom-right then bottom-left)
  const areaPath = `${path} L ${width} ${height} L 0 ${height} Z`;

  return (
    <div
      className="rounded-md border p-2.5"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-card) 70%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
          7-day sales
        </div>
        <div className="text-[10px] font-medium text-green-500 flex items-center gap-0.5">
          <TrendingUp className="w-2.5 h-2.5" /> +23.4%
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-12">
        <defs>
          <linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(var(--accent-primary))" stopOpacity="0.25" />
            <stop offset="100%" stopColor="rgb(var(--accent-primary))" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Filled area — fades in after the line draws */}
        <path
          d={areaPath}
          fill="url(#sparklineFill)"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.6s ease-out 1.4s',
          }}
        />
        {/* The line itself — uses stroke-dasharray trick to draw in */}
        <path
          d={path}
          fill="none"
          stroke="rgb(var(--accent-primary))"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 600,
            strokeDashoffset: inView ? 0 : 600,
            transition: 'stroke-dashoffset 1.6s cubic-bezier(0.22, 1, 0.36, 1) 0.4s',
          }}
        />
      </svg>
    </div>
  );
}

const ACTIVITY_FEED = [
  { icon: DollarSign, color: '#22c55e', text: 'Nike Dunk Low \'Panda\' sold', meta: '+$167.50' },
  { icon: Package, color: '#3b82f6', text: 'Charizard PSA 10 added', meta: '$1,240 cost' },
  { icon: Truck, color: '#a855f7', text: 'Package #4421 delivered', meta: 'USPS' },
  { icon: TrendingUp, color: '#f59e0b', text: 'New high — best sales week', meta: '12 sales' },
];

function RecentActivity({ inView }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ACTIVITY_FEED.length), 3000);
    return () => clearInterval(t);
  }, [inView]);

  const item = ACTIVITY_FEED[idx];
  const Icon = item.icon;

  return (
    <div
      className="rounded-md border p-2.5 min-h-[44px] relative overflow-hidden"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-card) 70%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
        Recent activity
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-2"
        >
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${item.color}22`, color: item.color }}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0 truncate text-xs" style={{ color: 'var(--color-text)' }}>
            {item.text}
          </div>
          <div className="text-xs font-medium tabular-nums" style={{ color: item.color }}>
            {item.meta}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ============================================================
 * FaqAccordion — answers common objections inline
 * ============================================================ */
const FAQ_ITEMS = [
  {
    q: 'Why not just use a spreadsheet?',
    a: 'Spreadsheets break the moment your inventory crosses a hundred SKUs. You lose photo tracking, automated profit calculations, package status, and you re-type everything manually from supplier emails. Nexus does all of that automatically — and your data lives in a real database, not a brittle file someone might accidentally save over.',
  },
  {
    q: 'Do you take a percentage of my sales?',
    a: 'No. Flat monthly subscription, that\'s it. We don\'t touch your money, we don\'t broker your sales, we don\'t take a cut. You keep 100% of every sale, every time.',
  },
  {
    q: 'What platforms does it work with?',
    a: 'Nexus is platform-agnostic — manage inventory you sell on eBay, StockX, Goat, Mercari, Whatnot, Facebook Marketplace, your own Shopify, in-person, or all of the above. The email scraper auto-imports order confirmations from any retailer (Nike, Adidas, Pokémon Center, Target, etc.) into your inventory.',
  },
  {
    q: 'Can I import my existing data?',
    a: 'Yes. CSV import is built in, and our email scraper can backfill past orders from your Gmail or IMAP inbox. Most users have their entire history imported in under five minutes.',
  },
  {
    q: 'What happens to my data if I cancel?',
    a: 'It stays yours. Export everything to CSV or JSON at any time, including after cancellation. No "data hostage" lock-in.',
  },
  {
    q: 'Is there a free trial?',
    a: 'The Free tier is permanent — 150 items, 50 sales, 50 expenses, no credit card. Upgrade when you outgrow it. No clock running down on you.',
  },
];

function FaqAccordion() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="space-y-2">
      {FAQ_ITEMS.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div
            key={i}
            className="rounded-md border overflow-hidden"
            style={{
              backgroundColor: 'var(--color-card)',
              borderColor: isOpen
                ? 'color-mix(in srgb, rgb(var(--accent-primary)) 35%, var(--color-border))'
                : 'var(--color-border)',
            }}
          >
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:opacity-90"
              aria-expanded={isOpen}
            >
              <span className="text-sm sm:text-base font-medium" style={{ color: 'var(--color-text)' }}>
                {item.q}
              </span>
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                style={{ color: 'var(--color-text-muted)' }}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-4 pb-4 text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                    {item.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
 * EmailImportShowcase — visualizes the auto-order-import flow
 *
 * Cycles through retailer emails on the left, with an animated
 * arrow + scanning state in the middle, "writing" a parsed
 * inventory entry on the right. No in-app screenshots — it's
 * all stylized so the marketing story doesn't depend on UI
 * staying pixel-stable over time.
 * ============================================================ */
const SAMPLE_EMAILS = [
  {
    retailer: 'Nike.com',
    color: '#000000',
    subject: 'Your Nike Air Max 97 order has shipped',
    parsed: {
      name: "Nike Air Max 97 'Silver Bullet'",
      sku: 'BV2647-001',
      cost: 170.00,
      qty: 1,
    },
  },
  {
    retailer: 'Pokémon Center',
    color: '#ee1515',
    subject: 'Order confirmation #PC-9824-1102',
    parsed: {
      name: 'Charizard ex Premium Collection',
      sku: 'POK-CHARV-PC',
      cost: 49.99,
      qty: 2,
    },
  },
  {
    retailer: 'StockX',
    color: '#006340',
    subject: 'Your StockX purchase has been verified',
    parsed: {
      name: 'Jordan 1 Retro High OG Chicago',
      sku: '555088-101',
      cost: 312.00,
      qty: 1,
    },
  },
  {
    retailer: 'Target',
    color: '#cc0000',
    subject: 'Your Target order is on the way',
    parsed: {
      name: 'Lego Technic 42158 NASA Rover',
      sku: 'TGT-42158',
      cost: 79.99,
      qty: 3,
    },
  },
];

function EmailImportShowcase({ isMobile }) {
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { once: false, margin: '-100px' });
  const [idx, setIdx] = useState(0);

  // Cycle through emails every ~3.5s when in view
  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SAMPLE_EMAILS.length), 3500);
    return () => clearInterval(t);
  }, [inView]);

  const active = SAMPLE_EMAILS[idx];
  const totalCost = active.parsed.cost * active.parsed.qty;

  return (
    <div ref={containerRef} className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-[1fr_auto_1.1fr] gap-4'} items-center`}>
      {/* LEFT — incoming email card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`email-${idx}`}
          initial={{ opacity: 0, x: isMobile ? 0 : -20, y: isMobile ? -10 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: isMobile ? 0 : -10, y: isMobile ? 10 : 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-md border p-4 shadow-lg"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          {/* Email header — retailer chip + "from" line */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: active.color }}
            >
              {active.retailer.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium" style={{ color: 'var(--color-text)' }}>
                {active.retailer}
              </div>
              <div className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                to: you@gmail.com
              </div>
            </div>
            <div className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>just now</div>
          </div>

          {/* Subject */}
          <div className="text-xs font-medium mb-2 truncate" style={{ color: 'var(--color-text)' }}>
            {active.subject}
          </div>

          {/* Faux body lines */}
          <div className="space-y-1.5">
            <div className="h-1.5 rounded-full w-full" style={{ backgroundColor: 'var(--color-bg-muted, var(--color-border))' }} />
            <div className="h-1.5 rounded-full w-5/6" style={{ backgroundColor: 'var(--color-bg-muted, var(--color-border))' }} />
            <div className="h-1.5 rounded-full w-4/6" style={{ backgroundColor: 'var(--color-bg-muted, var(--color-border))' }} />
          </div>

          {/* "Scanning" indicator */}
          <div className="flex items-center gap-1.5 mt-3 text-[10px]" style={{ color: 'rgb(var(--accent-primary))' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'rgb(var(--accent-primary))' }} />
            Scanning for order details…
          </div>
        </motion.div>
      </AnimatePresence>

      {/* MIDDLE — arrow with sparkle, animated */}
      <div className={`flex ${isMobile ? 'flex-row justify-center py-2' : 'flex-col items-center'} gap-1`}>
        <motion.div
          key={`arrow-${idx}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="flex items-center justify-center w-9 h-9 rounded-full"
          style={{
            backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 15%, var(--color-card))',
            border: '1px solid color-mix(in srgb, rgb(var(--accent-primary)) 35%, var(--color-border))',
          }}
        >
          <Sparkles className="w-4 h-4" style={{ color: 'rgb(var(--accent-primary))' }} />
        </motion.div>
        <div
          className={`${isMobile ? 'w-12 h-px' : 'w-px h-12'}`}
          style={{ backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, transparent)' }}
        />
        <ArrowRight
          className={`w-4 h-4 ${isMobile ? '' : 'rotate-90'}`}
          style={{ color: 'rgb(var(--accent-primary))' }}
        />
      </div>

      {/* RIGHT — parsed inventory entry */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`parsed-${idx}`}
          initial={{ opacity: 0, x: isMobile ? 0 : 20, y: isMobile ? -10 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: isMobile ? 0 : 10, y: isMobile ? 10 : 0 }}
          transition={{ duration: 0.3, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-md border p-4 shadow-lg"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, var(--color-border))',
          }}
        >
          {/* Success header */}
          <div className="flex items-center gap-1.5 mb-3 text-[10px] font-medium" style={{ color: 'rgb(34, 197, 94)' }}>
            <Check className="w-3 h-3" />
            ADDED TO INVENTORY
          </div>

          {/* Product name */}
          <div className="text-sm font-semibold mb-3 leading-snug" style={{ color: 'var(--color-text)' }}>
            {active.parsed.name}
          </div>

          {/* Parsed field rows */}
          <div className="space-y-1.5">
            <ParsedField label="SKU" value={active.parsed.sku} mono />
            <ParsedField label="Quantity" value={active.parsed.qty.toString()} />
            <ParsedField label="Cost (each)" value={`$${active.parsed.cost.toFixed(2)}`} />
            <ParsedField label="Total cost" value={`$${totalCost.toFixed(2)}`} accent />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ParsedField({ label, value, accent = false, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[11px]">
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span
        className={`font-medium tabular-nums ${mono ? 'font-mono text-[10px]' : ''}`}
        style={{ color: accent ? 'rgb(var(--accent-primary))' : 'var(--color-text)' }}
      >
        {value}
      </span>
    </div>
  );
}

/* ============================================================
 * Main HomePage
 * ============================================================ */
const HomePage = () => {
  const { currentUser, mfaRequired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('month');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [checkoutMessage, setCheckoutMessage] = useState(null);
  const isMobile = useIsMobile();
  const isLoggedIn = !!currentUser && !mfaRequired;

  // Deep-link landing: when a logged-out user opens an email link to a
  // protected route (e.g. /account#billing from the "Resubscribe to
  // Plus" CTA), App.js routes them here. We capture the intended URL,
  // open the login modal, and after they sign in we navigate to that
  // URL — preserving any hash so AccountPage opens the right tab.
  const [postLoginRedirect, setPostLoginRedirect] = useState(null);
  // Tracks which path we've already auto-prompted login for so closing
  // the modal doesn't re-open it in an infinite loop. Re-prompts when
  // the user navigates to a different protected path via address bar.
  const promptedForPathRef = useRef(null);

  useEffect(() => {
    if (isLoggedIn) return;
    if (authModalOpen) return;
    const path = location.pathname;
    // Explicit allowlist of protected routes that should auto-open the
    // login modal when a logged-out user lands on them (e.g. email link
    // to /account?tab=billing). Inverted from the prior "open unless
    // marketing root" logic — that was too aggressive and was opening
    // the modal on the marketing homepage in some edge cases. Anything
    // not in this list just renders the marketing HomePage as normal.
    const PROTECTED_PATH_PREFIXES = [
      '/account',
      '/dashboard',
      '/inventory',
      '/vault',
      '/sales',
      '/buyers',
      '/expenses',
      '/orders-tracking',
      '/reports',
      '/settings',
      '/admin-settings',
      '/product-scanner',
      '/archives',
      '/view',
      '/global-products-admin',
    ];
    const isProtected = PROTECTED_PATH_PREFIXES.some(
      (p) => path === p || path.startsWith(p + '/')
    );
    if (isProtected && promptedForPathRef.current !== path) {
      promptedForPathRef.current = path;
      // Preserve BOTH search params (?tab=billing) and hash (#billing)
      // — different email link trackers strip them differently, so we
      // keep both in case the inbound URL has either.
      setPostLoginRedirect(path + (location.search || '') + (location.hash || ''));
      setAuthModalMode('login');
      setSelectedTier(null);
      setAuthModalOpen(true);
    }
  }, [isLoggedIn, location.pathname, location.hash, location.search, authModalOpen]);

  // Auto-redirect after login. If a deep-link redirect was queued
  // (above), honor it. Otherwise default to /dashboard. The
  // location.state?.fromApp guard preserves the existing escape hatch
  // for in-app navigation back to the marketing site (footer link, etc.).
  useEffect(() => {
    if (!isLoggedIn) return;
    if (location.state?.fromApp) return;
    if (postLoginRedirect) {
      navigate(postLoginRedirect, { replace: true });
      setPostLoginRedirect(null);
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [isLoggedIn, navigate, location.state, postLoginRedirect]);

  useEffect(() => {
    document.title = 'Nexus - Inventory Management for Resellers';
  }, []);

  // Inject the small keyframes used by buttons (rest of motion is via framer)
  useEffect(() => {
    const styleId = 'nexus-homepage-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = animationStyles;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Checkout result + ?auth=login|signup deep-links from other pages
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const email = searchParams.get('email');
    const auth = searchParams.get('auth');

    if (checkout === 'success') {
      setCheckoutMessage({
        type: 'success',
        title: 'Payment Successful!',
        message: email
          ? `Check your email (${email}) to confirm your account.`
          : 'Check your email to confirm your account.',
      });
      if (typeof window.rewardful === 'function' && email) {
        window.rewardful('convert', { email });
      }
      setSearchParams({});
    } else if (checkout === 'canceled') {
      setCheckoutMessage({
        type: 'canceled',
        title: 'Checkout Canceled',
        message: 'Your payment was not completed.',
      });
      setSearchParams({});
    }

    if (auth === 'login') {
      setAuthModalMode('login');
      setSelectedTier(null);
      setAuthModalOpen(true);
      setSearchParams({});
    } else if (auth === 'signup') {
      setAuthModalMode('signup');
      setSelectedTier(null);
      setAuthModalOpen(true);
      setSearchParams({});
    } else if (auth === 'reset') {
      // Deep-linked from the mobile app's "Forgot password?" button.
      setAuthModalMode('reset');
      setSelectedTier(null);
      setAuthModalOpen(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // MFA reopens the modal so the user can enter their code
  useEffect(() => {
    if (mfaRequired) setAuthModalOpen(true);
  }, [mfaRequired]);

  const openSignup = () => { setAuthModalMode('signup'); setSelectedTier(null); setAuthModalOpen(true); };
  const openLogin = () => { setAuthModalMode('login'); setSelectedTier(null); setAuthModalOpen(true); };
  const scrollToPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

  const handleSelectPlan = (tierId, billingInterval) => {
    if (isLoggedIn) {
      navigate('/dashboard?upgrade=' + tierId);
      return;
    }
    setAuthModalMode('signup');
    setSelectedTier(tierId === 'free' ? null : tierId);
    setSelectedInterval(billingInterval || 'month');
    setAuthModalOpen(true);
  };

  /* -- pricing data --
   *
   * Reads from the single source of truth in src/config/pricing.js.
   * Pre-2026-05-30 this file kept its own hardcoded 4-tier array; now
   * we just project TIERS into the shape the JSX below expects. The
   * "Most Popular" badge is derived from tier.highlighted (only Plus
   * sets it in the 2-tier world).
   */
  const pricingTiers = TIER_ORDER.map((id) => {
    const t = TIERS[id];
    return {
      id: t.id,
      name: t.name,                         // "Free" / "Plus"
      price: { monthly: t.monthlyPrice, yearly: t.yearlyPrice },
      desc: t.description,
      features: t.features,
      cta: t.cta,
      highlighted: t.highlighted,
      badge: t.highlighted ? 'Most Popular' : null,
    };
  });

  /* ──────────────────────────────────────────────────────────
   * 1. NAV
   * ────────────────────────────────────────────────────────── */
  const renderNav = () => (
    <nav
      className="sticky top-0 z-50 backdrop-blur-md border-b"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-bg) 85%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo + wordmark. Real brand mark from /public, paired with the
            letter-spaced NEXUS caps used on share-card modals. */}
        <Link to="/" className="flex items-center gap-2" aria-label="Nexus home">
          <img
            src="/nexus-logo-only2.png"
            alt=""
            className="w-7 h-7 object-contain"
          />
          <span
            className="text-sm sm:text-base font-medium tracking-widest"
            style={{ color: 'var(--color-text)' }}
          >
            Nexus
          </span>
        </Link>

        {/* Desktop links */}
        {!isMobile && (
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <a href="#features" className="hover:text-theme transition-colors" style={{ color: 'inherit' }}>Features</a>
            <a href="#pricing" className="hover:text-theme transition-colors" style={{ color: 'inherit' }}>Pricing</a>
            <a href="#faq" className="hover:text-theme transition-colors" style={{ color: 'inherit' }}>FAQ</a>
          </div>
        )}

        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 text-sm rounded-md font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'rgb(var(--accent-primary))' }}
            >
              Go to App
            </button>
          ) : (
            <>
              {!isMobile && (
                <button
                  onClick={openLogin}
                  className="px-3 py-1.5 text-sm rounded-md font-medium transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Sign in
                </button>
              )}
              <button
                onClick={openSignup}
                className="px-3 py-1.5 text-sm rounded-md font-medium text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'rgb(var(--accent-primary))' }}
              >
                Get Started
              </button>
            </>
          )}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="p-1.5 rounded-md"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobile && mobileMenuOpen && (
          <motion.div
            id="mobile-nav-menu"
            role="navigation"
            aria-label="Mobile navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="px-6 py-3 space-y-2 text-sm">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Features</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Pricing</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>FAQ</a>
              {!isLoggedIn && (
                <button onClick={() => { openLogin(); setMobileMenuOpen(false); }} className="block w-full text-left py-1.5" style={{ color: 'var(--color-text-muted)' }}>Sign in</button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );

  /* ──────────────────────────────────────────────────────────
   * 2. HERO — copy + live dashboard widget side by side
   * ────────────────────────────────────────────────────────── */
  const renderHero = () => (
    <section
      className={`relative overflow-hidden px-4 sm:px-6 ${isMobile ? 'pt-10 pb-12' : 'pt-16 pb-20'}`}
    >
      {/* Ambient accent glow in the background */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgb(var(--accent-primary) / 0.18) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      <div className={`relative max-w-[1280px] mx-auto grid ${isMobile ? 'grid-cols-1 gap-10' : 'grid-cols-[1.05fr_1fr] gap-12'} items-center`}>
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`${isMobile ? 'text-center' : 'text-left'}`}
        >
          {/* Eyebrow chip */}
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium mb-4 ${isMobile ? 'mx-auto' : ''}`}
            style={{
              backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 10%, var(--color-card))',
              borderColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, var(--color-border))',
              color: 'rgb(var(--accent-primary))',
            }}
          >
            <Zap className="w-3 h-3" />
            Built by resellers, for resellers
          </div>

          <h1
            className={`${isMobile ? 'text-3xl' : 'text-4xl md:text-5xl lg:text-6xl'} font-bold leading-[1.05] tracking-tight mb-5`}
            style={{ color: 'var(--color-text)' }}
          >
            Inventory, sales, and profit —{' '}
            <span
              style={{
                background: `linear-gradient(90deg, rgb(var(--accent-primary)) 0%, color-mix(in srgb, rgb(var(--accent-primary)) 50%, #a78bfa) 50%, rgb(var(--accent-primary)) 100%)`,
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmer 5s linear infinite',
              }}
            >
              finally in one place
            </span>
            .
          </h1>

          <p
            className={`${isMobile ? 'text-base' : 'text-lg'} mb-7 max-w-[520px] ${isMobile ? 'mx-auto' : ''}`}
            style={{ color: 'var(--color-text-muted)' }}
          >
            Track every item, log every sale, watch every package — and see your real profit at the end of the month. No spreadsheets, no spreadsheet drama.
          </p>

          <div className={`flex flex-wrap gap-3 ${isMobile ? 'justify-center' : ''}`}>
            {isLoggedIn ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 rounded-md flex items-center gap-2 font-semibold text-white transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: 'rgb(var(--accent-primary))', animation: 'pulseGlow 3s ease-in-out infinite' }}
              >
                Go to App
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                <button
                  onClick={openSignup}
                  className="px-5 py-2.5 rounded-md flex items-center gap-2 font-semibold text-white transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: 'rgb(var(--accent-primary))', animation: 'pulseGlow 3s ease-in-out infinite' }}
                >
                  Start Free
                  <ArrowRight className="w-4 h-4" style={{ animation: 'bounceArrow 1.5s ease-in-out infinite' }} />
                </button>
                <button
                  onClick={scrollToPricing}
                  className="px-5 py-2.5 rounded-md font-medium transition-colors border"
                  style={{
                    color: 'var(--color-text)',
                    borderColor: 'var(--color-border)',
                    backgroundColor: 'var(--color-card)',
                  }}
                >
                  See pricing
                </button>
              </>
            )}
          </div>

          {/* Trust chips below CTAs */}
          <div className={`flex flex-wrap ${isMobile ? 'justify-center gap-3' : 'gap-5'} mt-6 text-xs`} style={{ color: 'var(--color-text-muted)' }}>
            <div className="flex items-center gap-1.5">
              <Check className="w-3 h-3" style={{ color: 'rgb(var(--accent-primary))' }} />
              Free plan, no credit card
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="w-3 h-3" style={{ color: 'rgb(var(--accent-primary))' }} />
              Set up in 60 seconds
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="w-3 h-3" style={{ color: 'rgb(var(--accent-primary))' }} />
              Cancel anytime
            </div>
          </div>
        </motion.div>

        {/* Right: live animated dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeroLiveDashboard />
        </motion.div>
      </div>
    </section>
  );

  // Social proof bar removed — was reading as a generic "platforms supported"
  // strip that didn't add much. The platform list lives in the FAQ answer
  // ("What platforms does it work with?") which gives it more context.


  /* ──────────────────────────────────────────────────────────
   * 4. VALUE PROPS — three pillars (replaces 7-card bento)
   * ────────────────────────────────────────────────────────── */
  const valueProps = [
    {
      icon: Package,
      title: 'Track everything',
      desc: 'Every item, sale, expense, and package — searchable, filterable, exportable. Photos and barcode scanning included.',
    },
    {
      icon: Sparkles,
      title: 'Automate the boring stuff',
      desc: 'Email scraper imports orders from your inbox. Package tracking auto-updates. Recurring expenses bill themselves.',
    },
    {
      icon: BarChart3,
      title: 'See your real profit',
      desc: 'Revenue minus COGS minus expenses, with ROI and margin by item, sale, or month. No more guessing.',
    },
  ];

  const renderValueProps = () => (
    <section id="features" className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-20'}`}>
      <div className="max-w-[1280px] mx-auto">
        <div className={`text-center ${isMobile ? 'mb-8' : 'mb-12'}`}>
          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
            Three things Nexus does better than anything else
          </h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: 'var(--color-text-muted)' }}>
            Not 47 features. Three things that compound into a real edge.
          </p>
        </div>

        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-3 gap-5'}`}>
          {valueProps.map((vp, i) => {
            const Icon = vp.icon;
            return (
              <motion.div
                key={vp.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-md border p-5 sm:p-6"
                style={{
                  backgroundColor: 'var(--color-card)',
                  borderColor: 'var(--color-border)',
                }}
              >
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 12%, transparent)',
                    color: 'rgb(var(--accent-primary))',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold mb-1.5" style={{ color: 'var(--color-text)' }}>
                  {vp.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                  {vp.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 5. EMAIL IMPORT SHOWCASE — visualizes auto order-import
   *    flow. No in-app screenshots; entire visual is built from
   *    typography + card mockups so the design stays stable.
   * ────────────────────────────────────────────────────────── */
  const renderEmailImport = () => (
    <section
      className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-20'} border-y`}
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-card) 30%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="max-w-[1280px] mx-auto">
        <div className={`${isMobile ? 'mb-8' : 'mb-12'} max-w-[700px]`}>
          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium mb-3"
            style={{
              backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 10%, var(--color-card))',
              borderColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, var(--color-border))',
              color: 'rgb(var(--accent-primary))',
            }}
          >
            <Mail className="w-3 h-3" />
            Auto order import
          </div>

          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
            Your inbox is already your buying log.<br />
            <span style={{ color: 'rgb(var(--accent-primary))' }}>We just read it for you.</span>
          </h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: 'var(--color-text-muted)' }}>
            Connect Gmail or any IMAP inbox. Every order confirmation — from Nike, Pokémon Center, StockX, Target, Walmart, every other retailer — gets parsed automatically and lands in your inventory with the product name, SKU, quantity, and total cost. Zero retyping.
          </p>
        </div>

        {/* The animated flow visualization */}
        <EmailImportShowcase isMobile={isMobile} />

        {/* Sub-bullets below the visual */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-3 gap-5'} mt-10 max-w-[900px]`}>
          {[
            { icon: Mail, title: 'Gmail + IMAP', desc: 'OAuth for Gmail, full IMAP for everyone else (iCloud, Outlook, Yahoo, your own server).' },
            { icon: Sparkles, title: 'Smart parsing', desc: 'Reads product name, SKU, quantity, price, tax, shipping — and the tracking number once it ships.' },
            { icon: Clock, title: 'Backfill or live', desc: 'Import your last year of orders in one pass, then watch new ones flow in automatically.' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 12%, transparent)',
                    color: 'rgb(var(--accent-primary))',
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-sm font-semibold mb-0.5" style={{ color: 'var(--color-text)' }}>
                    {item.title}
                  </div>
                  <div className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 6. MORE WAYS TO WORK — three additional features that don't
   *    rate a full dedicated section but deserve more than a
   *    footnote in the bento. Spreadsheet import, Discord bot,
   *    and the upcoming mobile app.
   * ────────────────────────────────────────────────────────── */
  const moreFeatures = [
    {
      icon: FileSpreadsheet,
      title: 'Bring your spreadsheet in',
      desc: "Paste a CSV or upload XLSX — we map columns automatically, even when your headers don't match ours. Bulk-edit before commit so nothing lands sideways.",
      cta: null,
    },
    {
      icon: DiscordMark,
      title: 'Manage from Discord',
      desc: "Slash commands to add items, log sales, check stock, and see today's deliveries — straight from your trading server. No tab-switching mid-conversation.",
      cta: { label: 'Join the server', href: 'https://discord.gg/F3m5t9kyyF', external: true },
    },
    {
      icon: Smartphone,
      title: 'Nexus on your phone',
      desc: 'Native iOS + Android apps with push notifications, barcode scanning, and quick-add from wherever you are. Same data, anywhere.',
      cta: null,
      comingSoon: true,
    },
  ];

  const renderMoreFeatures = () => (
    <section className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-20'}`}>
      <div className="max-w-[1280px] mx-auto">
        <div className={`text-center max-w-[680px] mx-auto ${isMobile ? 'mb-8' : 'mb-12'}`}>
          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
            And a few more ways to work
          </h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: 'var(--color-text-muted)' }}>
            Spreadsheet import, a Discord bot, and a mobile app on the way.
          </p>
        </div>

        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-3 gap-5'}`}>
          {moreFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.45, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-md border p-5 sm:p-6 flex flex-col"
                style={{
                  backgroundColor: 'var(--color-card)',
                  borderColor: 'var(--color-border)',
                }}
              >
                {/* "Coming Soon" pill — top-right corner, subtle */}
                {f.comingSoon && (
                  <div
                    className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full"
                    style={{
                      backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 15%, transparent)',
                      color: 'rgb(var(--accent-primary))',
                      border: '1px solid color-mix(in srgb, rgb(var(--accent-primary)) 30%, transparent)',
                    }}
                  >
                    Coming Soon
                  </div>
                )}

                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 12%, transparent)',
                    color: 'rgb(var(--accent-primary))',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>

                <h3 className="text-lg font-semibold mb-1.5" style={{ color: 'var(--color-text)' }}>
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed flex-1" style={{ color: 'var(--color-text-muted)' }}>
                  {f.desc}
                </p>

                {f.cta && (
                  <a
                    href={f.cta.href}
                    {...(f.cta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-medium transition-opacity hover:opacity-80"
                    style={{ color: 'rgb(var(--accent-primary))' }}
                  >
                    {f.cta.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 7. PRICING — gated by billing maintenance flag so the section
   *    can hide its CTAs (or render an inline maintenance banner)
   *    while admins swap Stripe products / env vars.
   * ────────────────────────────────────────────────────────── */
  const renderPricing = () => (
    <section id="pricing" className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-20'}`}>
      <div className="max-w-[1200px] mx-auto">
        {/* When maintenance is on, a friendly banner shows above the
            (still-visible-but-disabled) pricing cards so visitors know
            why the Get Started buttons are unresponsive. */}
        <BillingMaintenanceInlineBanner />
        <div className={`text-center ${isMobile ? 'mb-8' : 'mb-12'}`}>
          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
            Simple, transparent pricing
          </h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'} mb-6`} style={{ color: 'var(--color-text-muted)' }}>
            Start free, upgrade when you outgrow it. No credit card to start.
          </p>

          {/* Billing toggle */}
          <div
            className="inline-flex items-center gap-1 p-1 rounded-md border"
            style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
          >
            <button
              onClick={() => setBillingPeriod('monthly')}
              className="px-4 py-1.5 rounded text-sm font-medium transition-colors"
              style={{
                backgroundColor: billingPeriod === 'monthly' ? 'rgb(var(--accent-primary))' : 'transparent',
                color: billingPeriod === 'monthly' ? '#fff' : 'var(--color-text-muted)',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className="px-4 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5"
              style={{
                backgroundColor: billingPeriod === 'yearly' ? 'rgb(var(--accent-primary))' : 'transparent',
                color: billingPeriod === 'yearly' ? '#fff' : 'var(--color-text-muted)',
              }}
            >
              Yearly
              {/* Real savings with the auto-applied yearly coupon:
                  $10×12 = $120 list, coupon brings it to $96 = $24 saved
                  (20%). Old "-15%" label was a leftover from the 4-tier
                  Pro $15/mo → $140/yr pricing and is no longer accurate. */}
              <span className="text-[10px] bg-green-500/20 text-green-500 px-1.5 py-0.5 rounded-full">−20%</span>
            </button>
          </div>
        </div>

        {/* Two-card layout (Free + Plus). The grid widens to max-w-[720px]
            so two cards don't stretch absurdly wide on desktop. */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-5 max-w-[720px] mx-auto'}`}>
          {pricingTiers.map((tier) => {
            const isHL = tier.highlighted;
            return (
              <div
                key={tier.id}
                className="relative p-5 rounded-md border transition-colors"
                style={{
                  backgroundColor: 'var(--color-card)',
                  borderColor: isHL
                    ? 'color-mix(in srgb, rgb(var(--accent-primary)) 50%, var(--color-border))'
                    : 'var(--color-border)',
                  boxShadow: isHL ? '0 8px 32px rgb(var(--accent-primary) / 0.15)' : undefined,
                }}
              >
                {tier.badge && (
                  <div
                    className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 text-[11px] font-medium rounded-full text-white"
                    style={{ backgroundColor: 'rgb(var(--accent-primary))' }}
                  >
                    {tier.badge}
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{tier.name}</h3>
                </div>
                <div className="mb-1">
                  <span className="text-3xl font-bold" style={{ color: 'var(--color-text)' }}>
                    ${billingPeriod === 'monthly' ? tier.price.monthly : tier.price.yearly}
                  </span>
                  <span className="text-sm ml-1" style={{ color: 'var(--color-text-muted)' }}>
                    /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>{tier.desc}</p>
                <PricingCardCta
                  tier={tier}
                  isHL={isHL}
                  onSelect={() => handleSelectPlan(tier.id, billingPeriod === 'monthly' ? 'month' : 'year')}
                />
                <ul className="space-y-2">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(var(--accent-primary))' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--color-text-muted)' }}>
          All plans include SSL encryption, daily backups, and email support.
        </p>
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 7. FAQ — accordion, opens with first item visible
   * ────────────────────────────────────────────────────────── */
  const renderFaq = () => (
    <section
      id="faq"
      className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-20'} border-y`}
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-card) 30%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="max-w-[800px] mx-auto">
        <div className={`text-center ${isMobile ? 'mb-8' : 'mb-10'}`}>
          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
            Questions, answered
          </h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: 'var(--color-text-muted)' }}>
            The stuff people usually want to know before signing up.
          </p>
        </div>

        <FaqAccordion />
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 8. FINAL CTA — full-bleed accent gradient
   * ────────────────────────────────────────────────────────── */
  const renderFinalCTA = () => (
    <section className={`px-4 sm:px-6 ${isMobile ? 'py-14' : 'py-24'}`}>
      <div className="max-w-[1000px] mx-auto">
        <div
          className="relative overflow-hidden rounded-md border p-8 sm:p-12 text-center"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, rgb(var(--accent-primary)) 12%, var(--color-card)) 0%, var(--color-card) 70%)`,
            borderColor: 'color-mix(in srgb, rgb(var(--accent-primary)) 30%, var(--color-border))',
          }}
        >
          {/* Background glow */}
          <div
            className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgb(var(--accent-primary) / 0.3) 0%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />

          <div className="relative z-10">
            <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-3 tracking-tight`} style={{ color: 'var(--color-text)' }}>
              Stop guessing what you made this month
            </h2>
            <p className={`${isMobile ? 'text-base' : 'text-lg'} mb-7 max-w-[560px] mx-auto`} style={{ color: 'var(--color-text-muted)' }}>
              Set up your account in 60 seconds. Free forever for up to 150 items — no credit card.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={isLoggedIn ? () => navigate('/dashboard') : openSignup}
                className="px-6 py-3 rounded-md flex items-center gap-2 font-semibold text-white transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: 'rgb(var(--accent-primary))', animation: 'pulseGlow 3s ease-in-out infinite' }}
              >
                {isLoggedIn ? 'Go to App' : 'Get Started Free'}
                <ArrowRight className="w-4 h-4" style={{ animation: 'bounceArrow 1.5s ease-in-out infinite' }} />
              </button>
              <button
                onClick={scrollToPricing}
                className="px-6 py-3 rounded-md font-medium transition-colors border"
                style={{
                  color: 'var(--color-text)',
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-card)',
                }}
              >
                See pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  /* ──────────────────────────────────────────────────────────
   * 9. FOOTER — 4-column, token colors
   * ────────────────────────────────────────────────────────── */
  const renderFooter = () => (
    <footer
      className="px-4 sm:px-6 py-10 border-t"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="max-w-[1280px] mx-auto">
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-6' : 'grid-cols-4 gap-8'}`}>
          {/* Brand */}
          <div className={`${isMobile ? 'col-span-2' : ''}`}>
            <div className="flex items-center gap-2 mb-3">
              <img src="/nexus-logo-only2.png" alt="" className="w-6 h-6 object-contain" />
              <span
                className="text-sm font-medium tracking-widest"
                style={{ color: 'var(--color-text)' }}
              >
                Nexus
              </span>
            </div>
            <p className="text-xs leading-relaxed max-w-[280px]" style={{ color: 'var(--color-text-muted)' }}>
              Inventory management built for resellers who treat it like a real business.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-md border flex items-center justify-center transition-colors hover:opacity-80"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                aria-label="Twitter"
              >
                <Twitter className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text)' }}>Product</h4>
            <ul className="space-y-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <li><a href="#features" className="hover:opacity-80">Features</a></li>
              <li><a href="#pricing" className="hover:opacity-80">Pricing</a></li>
              <li><a href="#faq" className="hover:opacity-80">FAQ</a></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text)' }}>Resources</h4>
            <ul className="space-y-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <li><Link to="/docs" className="hover:opacity-80">Documentation</Link></li>
              <li><Link to="/support" className="hover:opacity-80">Support</Link></li>
              <li><a href="https://discord.gg/F3m5t9kyyF" target="_blank" rel="noopener noreferrer" className="hover:opacity-80">Discord</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text)' }}>Legal</h4>
            <ul className="space-y-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <li><Link to="/privacy" className="hover:opacity-80">Privacy</Link></li>
              <li><Link to="/terms" className="hover:opacity-80">Terms</Link></li>
              <li><Link to="/cookies" className="hover:opacity-80">Cookies</Link></li>
            </ul>
          </div>
        </div>

        <div
          className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
        >
          <span>© {new Date().getFullYear()} Nexus Inventory Manager · A product of NOTEAZY L.L.C. All rights reserved.</span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            SSL encrypted · Daily backups
          </span>
        </div>
      </div>
    </footer>
  );

  /* ============================================================
   *  MAIN RENDER
   * ============================================================ */
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', fontFamily: "'Inter', sans-serif" }}
    >
      {/* Checkout result banner */}
      {checkoutMessage && (
        <div
          className={`fixed ${isMobile ? 'top-14' : 'top-16'} left-0 right-0 z-40 px-6 py-3 border-b`}
          style={
            checkoutMessage.type === 'success'
              ? { backgroundColor: 'color-mix(in srgb, #22c55e 10%, transparent)', borderColor: 'color-mix(in srgb, #22c55e 20%, transparent)' }
              : { backgroundColor: 'color-mix(in srgb, #f59e0b 10%, transparent)', borderColor: 'color-mix(in srgb, #f59e0b 20%, transparent)' }
          }
        >
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            {checkoutMessage.type === 'success'
              ? <Mail className="w-4 h-4 text-green-500" />
              : <AlertCircle className="w-4 h-4 text-amber-500" />}
            <div className="flex-1 text-sm">
              <span className={`font-medium ${checkoutMessage.type === 'success' ? 'text-green-500' : 'text-amber-500'}`}>{checkoutMessage.title}</span>
              <span className="ml-2" style={{ color: 'var(--color-text-muted)' }}>{checkoutMessage.message}</span>
            </div>
            <button onClick={() => setCheckoutMessage(null)} style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {renderNav()}
      {renderHero()}
      {renderValueProps()}
      {renderEmailImport()}
      {renderMoreFeatures()}
      {renderPricing()}
      {renderFaq()}
      {renderFinalCTA()}
      {renderFooter()}

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
        selectedTier={selectedTier}
        selectedInterval={selectedInterval}
      />
    </div>
  );
};

export default HomePage;
